import Vue from 'vue'
import store from '~/store'
import Meta from 'vue-meta'
import allRoutes from './routes'
import RouterComponent from 'vue-router'
import { sync } from 'vuex-router-sync'
import { randomString } from '~/plugins/helper'

Vue.use(Meta)
Vue.use(RouterComponent)

let timer = null

/**
 * Set the application layout from the matched page component.
 * @param {VueRouter} router
 * @param {Object} currentTo
 * @param {Object} currentFrom
 */
function setLayout(router, currentTo, currentFrom) {
  const promise = new Promise((resolve, reject) => {
    timer = setInterval(async () => {
      if (router.app) {
        const componentMatches = await resolveComponents(router.getMatchedComponents(currentTo))
        const prevMatched = await resolveComponents(router.getMatchedComponents(currentFrom))
        let diffed = false

        const activated = componentMatches.filter((c, i) => {
          return diffed || (diffed = prevMatched[i] !== c)
        })

        try {
          const componentMatch = activated.pop()
          const handler = componentMatch ? componentMatch.handler : null
          if (!(handler instanceof Object)) {
            resolve(null)
          }
          if (!(handler.component instanceof Promise)) {
            resolve(null)
          }

          const component = await Promise.resolve(handler.component)
          if (router.app.$el && component) {
            let layout = (component && component.layout) ? component.layout : null
            if (store.getters.authCheck === true && layout === 'login') {
              layout = 'default'
            } else if (store.getters.authCheck === false && !layout) {
              layout = 'login'
            }

            resolve(layout)
          }
        } catch (e) {
          reject(e)
        }
      }
    }, 0)
  })
  promise.then((layout) => {
    clearInterval(timer)
    // Set application layout.
    router.app.setLayout(layout)
  }).catch(err => {
    // Set application layout.
    router.app.setLayout(null)

    console.error(err)
    clearInterval(timer)
  })
}

/**
 * Create a new router instance.
 * @param  {Array} routes
 * @return {VueRouter}
 */
function make(routes) {
  const router = new RouterComponent({
    routes,
    scrollBehavior,
    mode: 'history',
  })

  // Register before guard.
  router.beforeEach(async (to, from, next) => {
    if (!store.getters.authCheck && store.getters.authToken) {
      try {
        await store.dispatch('fetchUser')
        if (store.getters.faAuthCheck) {
          await store.dispatch('isAuthorized')
        }
      } catch (e) {
        console.error(e)
      }
    }

    store.dispatch('setStateRoute', { ...to, hash: randomString(20) })

    // Detect and trigger hash changes
    window.addEventListener('popstate', () => {
      store.dispatch('setUpdateStatus', randomString(20))
    })

    setLayout(router, to, from)

    next()
  })

  return router
}

/**
 * Apply beforeEnter guard to the routes.
 *
 * @param  {Array} routes
 * @param  {Function} beforeEnter
 * @return {Array}
 */
function beforeEnter(routes, beforeEnter) {
  return routes.map(route => {
    return { ...route, beforeEnter }
  })
}

/**
 * Redirect to login if guest.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function authGuard(routes) {
  return beforeEnter(routes, (to, from, next) => {
    if (store.getters.authCheck && store.getters.faAuthCheck && to.name === 'two_step') {
      next({ name: 'home.principal' })
    } else if ((store.getters.authCheck && !store.getters.faAuthCheck) && (to.name !== 'two_step' && to.name !== 'terms_and_conditions_use')) {
      next({ name: 'terms_and_conditions_use' })
    }

    if (!store.getters.authCheck) {
      next({ name: 'login' })
    } else {
      next()
    }
  })
}

/**
 * Redirect home if authenticated.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function guestGuard(routes) {
  return beforeEnter(routes, async (to, from, next) => {
    const componentMatches = await resolveComponents(router.getMatchedComponents({ ...to }))
    const componentMatch = componentMatches.pop()
    const handler = componentMatch ? componentMatch.handler : null
    const component = await Promise.resolve((handler) ? handler.component : null)
    if (component && component.layout && component.layout === 'guest') {
      next()
    } else {
      if (store.getters.authCheck) {
        next({ name: 'home.principal' })
      } else {
        next()
      }
    }
  })
}

/**
 * Resolve async components.
 *
 * @param  {Array} components
 * @return {Array}
 */
function resolveComponents(components) {
  return Promise.all(components.map(component => {
    return typeof component === 'function' ? component() : component
  }))
}

/**
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior(to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition
  }

  if (to.hash) {
    return {
      selector: to.hash,
      behavior: 'smooth',
    }
  }

  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ x: 0, y: 0 })
    }, 190)
  })
}

const router = make(
  allRoutes({ authGuard, guestGuard }),
)

const methods = [
  'push',
  'replace',
]

methods.forEach((method) => {
  const originalMethod = RouterComponent.prototype[method]
  RouterComponent.prototype[method] = function m(location) {
    return originalMethod.call(this, location).catch((error) => {
      if (error.name !== 'NavigationDuplicated') {
        // Captura la excepción solo si no es 'NavigationDuplicated'
        console.error('Error de navegación:', error)
      }
    })
  }
})

sync(store, router)

export default router
